/* Reusable, opt-in image magnifier (loupe).
 *
 * Usage: add `data-magnify` to any <img>. Optional tuning:
 *   data-magnify-size="150"   — lens diameter in px (default 150).
 *   data-magnify-zoom="2.5"   — magnification factor (default 2.5).
 *   data-magnify-src="..."    — high-res source URL. Falls back to
 *                               img.currentSrc / img.src. For Shopify CDN
 *                               URLs containing ?width=N, the script
 *                               transparently rewrites to ?width=2000 so
 *                               the magnified view is high-res.
 *
 * Desktop-only — gated by `matchMedia('(hover: hover) and (pointer: fine)')`
 * so it never fires on touch devices. Listens to that mediaquery to attach
 * and detach cleanly when the user toggles between desktop and emulated
 * mobile in DevTools.
 *
 * Re-entry: dynamically-injected images can call
 *   window.Sirge_imageMagnifier.refresh()
 * to wire up. Single binding per image is enforced via __sirgeMagnifyBound.
 *
 * Magnifier survives runtime src changes on the underlying <img> (variant
 * swaps, thumbnail clicks, etc.) via a MutationObserver on the `src` and
 * `srcset` attributes. No call-site changes required.
 */
(function () {
  var LENS_CLASS = 'Sirge_magnify-lens';
  var WRAP_CLASS = 'Sirge_magnify-wrap';
  var STYLE_ID = 'Sirge_magnify-style';

  // Merchant-controlled defaults emitted by layout/theme.liquid before this
  // script runs (window.Sirge_imageMagnifierDefaults). The hardcoded numbers
  // below are the last-resort fallback used if the theme settings group
  // hasn't been wired (e.g. on a brand-new install before settings save).
  var THEME_DEFAULTS = (window.Sirge_imageMagnifierDefaults) || {};
  var DEFAULT_SIZE = THEME_DEFAULTS.size || 150;
  var DEFAULT_ZOOM = THEME_DEFAULTS.zoom || 2.5;
  var DEFAULT_MAGNIFY_WIDTH = THEME_DEFAULTS.magnifyWidth || 2000;
  var DEFAULT_SHAPE = THEME_DEFAULTS.shape || 'circle';
  // Border width may legitimately be 0, so check for undefined explicitly
  // rather than truthy-fall through to the default.
  var DEFAULT_BORDER_WIDTH =
    typeof THEME_DEFAULTS.borderWidth === 'number' ? THEME_DEFAULTS.borderWidth : 2;
  var DEFAULT_BORDER_COLOR = THEME_DEFAULTS.borderColor || 'rgba(255,255,255,0.9)';

  var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Inject the shared lens stylesheet once.
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    // Shape (border-radius) and border are set per-lens inline because they're
    // merchant-configurable. The stylesheet only carries the structural rules
    // that never vary. `box-sizing: border-box` keeps the rendered lens the
    // configured size regardless of border width.
    style.textContent =
      '.' + WRAP_CLASS + '{position:relative;display:inline-block;line-height:0;}' +
      '.' + LENS_CLASS + '{position:absolute;pointer-events:none;box-sizing:border-box;' +
      'box-shadow:0 0 12px rgba(0,0,0,0.3);background-repeat:no-repeat;' +
      'opacity:0;transition:opacity .12s ease;z-index:10;}';
    document.head.appendChild(style);
  }

  // Resolve the URL used for the magnified background. Prefers an explicit
  // override, falls back to whatever the browser currently picked from srcset.
  function resolveSrc(img) {
    var url = img.getAttribute('data-magnify-src') || img.currentSrc || img.src || '';
    if (!url) return '';
    // Shopify CDN: bump ?width=N to a higher value so the magnified view is
    // not pixelated. Non-CDN URLs pass through unchanged.
    return url.replace(/([?&]width=)\d+/, '$1' + DEFAULT_MAGNIFY_WIDTH);
  }

  // Pick the element to host the absolutely-positioned lens. If the image's
  // parent is already positioned, attach there to avoid restructuring the
  // DOM. Otherwise wrap the image in a <span> so we have a positioning
  // ancestor we control.
  function ensurePositioningHost(img) {
    var parent = img.parentNode;
    if (!parent) return null;
    var computed = window.getComputedStyle(parent);
    if (
      computed.position === 'relative' ||
      computed.position === 'absolute' ||
      computed.position === 'fixed' ||
      computed.position === 'sticky'
    ) {
      return parent;
    }
    if (parent.classList && parent.classList.contains(WRAP_CLASS)) {
      return parent;
    }
    var wrap = document.createElement('span');
    wrap.className = WRAP_CLASS;
    parent.insertBefore(wrap, img);
    wrap.appendChild(img);
    return wrap;
  }

  function attach(img) {
    if (!img || img.__sirgeMagnifyBound) return;
    if (!hoverMQ.matches) return;

    injectStyle();

    var host = ensurePositioningHost(img);
    if (!host) return;

    var size = parseInt(img.getAttribute('data-magnify-size'), 10) || DEFAULT_SIZE;
    var zoom = parseFloat(img.getAttribute('data-magnify-zoom')) || DEFAULT_ZOOM;

    var lens = document.createElement('div');
    lens.className = LENS_CLASS;
    lens.style.width = size + 'px';
    lens.style.height = size + 'px';
    lens.style.borderRadius = DEFAULT_SHAPE === 'square' ? '0' : '50%';
    if (DEFAULT_BORDER_WIDTH > 0) {
      lens.style.border = DEFAULT_BORDER_WIDTH + 'px solid ' + DEFAULT_BORDER_COLOR;
    } else {
      lens.style.border = 'none';
    }
    host.appendChild(lens);

    var currentBgUrl = '';
    var ready = false;          // Magnified source loaded and ready to display.
    var preloader = null;        // In-flight <Image> for the magnified source.

    // Preload the magnified source. The lens stays hidden until this completes
    // so the customer never sees a blank/transparent circle during the
    // network round-trip for the high-res (often 2000 px) CDN image.
    function preloadMagnified() {
      var url = resolveSrc(img);
      if (!url) {
        ready = false;
        return;
      }
      if (url === currentBgUrl) {
        // Already loaded (or in-flight from a prior call) — nothing to do.
        return;
      }
      currentBgUrl = url;
      ready = false;
      lens.style.backgroundImage = 'url("' + url + '")';

      // Cancel the previous loader's callback to avoid a stale "ready" flip
      // when the user switches variants mid-preload.
      if (preloader) preloader.onload = preloader.onerror = null;

      preloader = new Image();
      if ('fetchPriority' in preloader) {
        // We are not the LCP — let the visible <img> grab high priority first.
        preloader.fetchPriority = 'low';
      }
      preloader.onload = function () {
        // Race-protect: only mark ready if the resolved URL is still this one.
        if (resolveSrc(img) === url) ready = true;
      };
      preloader.onerror = function () {
        // Leave ready=false. Next hover will retry on src mutation.
      };
      preloader.src = url;
    }

    function showLens() {
      lens.style.opacity = '1';
    }
    function hideLens() {
      lens.style.opacity = '0';
    }

    function onEnter() {
      // The src may have updated since attach (lazy decode etc.). Re-resolve.
      preloadMagnified();
      if (ready) showLens();
    }
    function onLeave() {
      hideLens();
    }
    function onMove(e) {
      var rect = img.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      // Cursor outside the image bounds — or magnified source not ready yet.
      if (!ready || x < 0 || y < 0 || x > rect.width || y > rect.height) {
        hideLens();
        return;
      }
      showLens();
      lens.style.left = (x - size / 2) + 'px';
      lens.style.top = (y - size / 2) + 'px';
      var bgW = rect.width * zoom;
      var bgH = rect.height * zoom;
      lens.style.backgroundSize = bgW + 'px ' + bgH + 'px';
      lens.style.backgroundPosition =
        '-' + (x * zoom - size / 2) + 'px -' + (y * zoom - size / 2) + 'px';
    }

    // Kick off the preload immediately so the lens is ready by the time the
    // customer's cursor reaches the image.
    preloadMagnified();

    img.addEventListener('mouseenter', onEnter);
    img.addEventListener('mouseleave', onLeave);
    img.addEventListener('mousemove', onMove);

    // Stay in sync when src/srcset mutate (variant swaps, thumb clicks).
    var observer = new MutationObserver(function (mutations) {
      // Only react to src/srcset (not data-magnify-src) so we don't loop on
      // our own attribute removal below.
      var srcChanged = false;
      for (var i = 0; i < mutations.length; i++) {
        var name = mutations[i].attributeName;
        if (name === 'src' || name === 'srcset') {
          srcChanged = true;
          break;
        }
      }
      if (!srcChanged) return;

      // The initial `data-magnify-src` was the original product's featured
      // image. After the visible `src` changes (variant swap, thumb click),
      // that hint is stale — drop it so resolveSrc() falls through to the
      // live `currentSrc`/`src` and the lens reflects the CURRENT image.
      if (img.hasAttribute('data-magnify-src')) {
        img.removeAttribute('data-magnify-src');
      }

      // New image incoming — drop the ready flag and preload the new source.
      currentBgUrl = '';
      ready = false;
      hideLens();
      preloadMagnified();
    });
    observer.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });

    img.__sirgeMagnifyBound = true;
    img.__sirgeMagnifyCleanup = function () {
      img.removeEventListener('mouseenter', onEnter);
      img.removeEventListener('mouseleave', onLeave);
      img.removeEventListener('mousemove', onMove);
      observer.disconnect();
      if (lens.parentNode) lens.parentNode.removeChild(lens);
      img.__sirgeMagnifyBound = false;
      delete img.__sirgeMagnifyCleanup;
    };
  }

  function detach(img) {
    if (img && img.__sirgeMagnifyCleanup) img.__sirgeMagnifyCleanup();
  }

  function scan() {
    var imgs = document.querySelectorAll('img[data-magnify]');
    for (var i = 0; i < imgs.length; i++) attach(imgs[i]);
  }

  function teardownAll() {
    var imgs = document.querySelectorAll('img[data-magnify]');
    for (var i = 0; i < imgs.length; i++) detach(imgs[i]);
  }

  // React to capability changes — desktop ↔ mobile emulation toggles in
  // DevTools, or a stylus/trackpad being plugged in.
  function onHoverChange(ev) {
    if (ev.matches) {
      scan();
    } else {
      teardownAll();
    }
  }
  if (hoverMQ.addEventListener) {
    hoverMQ.addEventListener('change', onHoverChange);
  } else if (hoverMQ.addListener) {
    // Safari < 14 fallback.
    hoverMQ.addListener(onHoverChange);
  }

  function init() {
    if (hoverMQ.matches) scan();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Sirge_imageMagnifier = {
    attach: attach,
    detach: detach,
    refresh: scan,
  };
})();
